import React from 'react';
import { Card } from '../ui';
import { Submission } from '../../store/submission/types';
import { ValidationStatus } from '../../store/form/types';
import useTheme from '@material-ui/core/styles/useTheme';
import styled, { ThemeProvider } from 'styled-components';
import { RowAction } from '../types';
import StatusLabel from '../StatusLabel/StatusLabel';
import { useTranslation } from 'react-i18next';
import { getFullName } from '../../helpers/users';
import Payment from '../Payment/Payment';
import { Role } from '../../store/conference/types';
import { SubmissionPermissionManager } from '../../helpers/permissions';
import { getPaperStatusName } from '../../helpers/submission';

interface Props {
  submission: Submission;
  actions: RowAction[];
  canViewAuthors: boolean;
  validationStatus?: { title: string; status: ValidationStatus };
  authorConfirmation: boolean;
  personId: number;
  role: Role;
}

const SubmissionCard: React.FC<Props> = ({
  submission,
  actions,
  validationStatus,
  canViewAuthors,
  authorConfirmation,
  personId,
  role,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const spm = new SubmissionPermissionManager(submission);

  /* Show submission details ONLY if author is confirmed (and requires confirmation) */
  let details = undefined;
  if (role.type != 'author' || (spm.isAuthor(personId, role) && !spm.isPendingAuthor(personId, role))) {
    details = (
      <>
        {validationStatus ? (
          <li className="mb-1">
            <StatusLabel label={validationStatus.title} validationStatus={validationStatus.status} />
          </li>
        ) : (
          <li className="mb-1">
            <strong>{t('Paper status') + ': '}</strong>
            <span>{getPaperStatusName(submission)}</span>
          </li>
        )}
        {role.type == 'author' && <Payment submission={submission} personId={personId} />}
      </>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <StyledCard className="mx-2 h-full">
        <StyledTitle className="flex justify-between items-center mb-3">
          <div className="text-sm font-bold">
            #{submission.external_id} - {submission.title ?? t('Untitled paper')}
          </div>
        </StyledTitle>

        <StyledUList className="mb-2 square list-inside text-sm">
          {submission.authors.length > 0 && canViewAuthors && (
            <li className="mb-1">
              <strong>{t('Authors') + ': '}</strong>
              {submission.authors
                .map(
                  (author) =>
                    `${getFullName(author)}${
                      authorConfirmation && author.pending_confirmation ? ` (${t('Pending')})` : ''
                    }`,
                )
                .join(', ')}
            </li>
          )}
          {details}
        </StyledUList>

        <div className="text-sm absolute bottom-4 flex justify-between w-full left-0 px-5 flex-wrap actions-wrapper">
          {actions.map((action, index) => (
            <div key={index} className="mt-1 simple-action-wrapper">
              {action.element}
            </div>
          ))}
        </div>
      </StyledCard>
    </ThemeProvider>
  );
};

export default SubmissionCard;

const StyledTitle = styled.div`
  border-bottom: 1px dashed ${(props) => props.theme.palette.secondary.dark};
`;

const StyledUList = styled.ul`
  list-style-type: square;
  margin-bottom: 4rem;

  .submissionPaymentWrapper {
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    width: calc(100% - 0.9rem);

    .submissionPaymentText {
      .pendingPayment {
        color
      }
    }

    .submissionPaymentButton {
      padding-top: 0.2rem;
      padding-bottom: 0.2rem;
      padding-left: 1.5rem;
      padding-right: 1.5rem;
      height: 1.9rem;
    }
  }
`;

const StyledCard = styled(Card)`
  min-height: 16.6rem;

  .actions-wrapper:has(.pending-alert) {
    position: relative;
    bottom: 0;
    padding: 0;

    .simple-action-wrapper {
      width: 100%;

      .pending-alert {
        p {
          width: 11rem;
        }

        .content-wrapper {
          margin-right: 0;
          width: 100%;
        }

        button {
          width: fit-content !important;
          padding-left: 1.5rem !important;
          padding-right: 1.5rem !important;
        }
      }
    }

    .alert {
      display: flex;
      justify-content: space-between;
    }
  }
`;
